// =============================================================================
// forecastWeather.js — the live engine's WEATHER signal, rebuilt on our side.
// Lane BQ, Run #14, 13 Sep 2026.
//
// PURE and network-free, except fetchOpenMeteoDay at the bottom (which takes an
// injectable fetch so it can be unit-tested with a fake). It mirrors two pieces of
// ManchesterForecastEngine.js EXACTLY — getWeather() and the weather branch of
// getContextMultipliers() — same request, same thresholds, same rules for "the
// API said nothing". Nothing here is tuned; every number is the live engine's, copied.
//
// WHAT THE LIVE ENGINE DOES (read 13 Sep 2026, read-only):
//   * ONE request per forecast, for the TARGET date only, at Manchester's coordinates
//     (Trafford and Arndale share the one point):
//       https://api.open-meteo.com/v1/forecast?latitude=53.4808&longitude=-2.2426
//         &daily=precipitation_sum,sunshine_duration&timezone=Europe/London
//         &start_date=<D>&end_date=<D>
//   * isRainy = precipitation_sum > 2 (mm).  isSunny = sunshine_duration > 28800 (s = 8 h).
//     Rain wins when both are true (an else-if). A null value compares false in JS,
//     so a day the API cannot score is "neither" and shows NO chip at all.
//   * HTTP not-200, or a body with no daily.precipitation_sum → "unavailable": the
//     chip "Weather unavailable" (effect 0) is shown ONLY when the target is at most
//     14 days ahead — Math.round((target − now) / 86400000) <= 14 — so every PAST
//     date qualifies and the far future gets nothing.
//   * A THROWN fetch (network, timeout, unparseable JSON) is swallowed by the engine's
//     try/catch and adds NOTHING — not even the chip.
//   * open-meteo answers roughly today−93 … today+15 (probed 13 Sep 2026: "allowed
//     range 2026-06-12 to 2026-09-28"). So the live engine DOES apply Rain/Sunny to a
//     PAST target inside that window (31 Aug 2026 was live-flagged Rain +0.10), and a
//     date outside it gets a 400 → "unavailable".
//
// The reader (forecastReader.js) uses a stored reading from migration 1321 when the
// nightly importer has one, and otherwise makes THIS exact call itself — the same
// thing the engine does on every build.
// =============================================================================

// The engine's coordinates (Manchester city centre) and thresholds, verbatim.
export const MANCHESTER = { latitude: 53.4808, longitude: -2.2426 };
export const OPEN_METEO_BASE = "https://api.open-meteo.com/v1/forecast";
export const ENGINE_DAILY = "precipitation_sum,sunshine_duration";
// Extra columns the nightly importer stores for a human reading the table. They never
// enter the classification (which only ever reads the two engine variables above).
export const EXTRA_DAILY = "precipitation_probability_max,temperature_2m_max,weather_code";
export const RAIN_MM_OVER = 2;          // isRainy  : precipitation_sum > 2 mm
export const SUNSHINE_S_OVER = 28800;   // isSunny  : sunshine_duration > 28800 s (8 hours)
export const UNAVAILABLE_FLAG_MAX_DAYS_AHEAD = 14;

// The exact request string the engine builds (byte-identical when from === to and no
// extras). The importer passes a range and asks for the extras; the reader's direct
// call is the plain single-day form.
export function openMeteoUrl(from, to = from, { extras = false } = {}) {
  const daily = extras ? ENGINE_DAILY + "," + EXTRA_DAILY : ENGINE_DAILY;
  return OPEN_METEO_BASE +
    "?latitude=" + MANCHESTER.latitude + "&longitude=" + MANCHESTER.longitude +
    "&daily=" + daily +
    "&timezone=Europe/London" +
    "&start_date=" + String(from).slice(0, 10) + "&end_date=" + String(to).slice(0, 10);
}

// The engine's parse, generalised to a range: null when the body has no
// daily.precipitation_sum (the engine's "unavailable"), else one reading per index.
// `sunshine` reproduces `data.daily.sunshine_duration ? …[i] : 0`.
export function parseOpenMeteoDaily(json) {
  if (!json || typeof json !== "object" || !json.daily || !json.daily.precipitation_sum) return null;
  const d = json.daily;
  const at = (arr, i) => (Array.isArray(arr) ? (arr[i] === undefined ? null : arr[i]) : null);
  const n = Array.isArray(d.precipitation_sum) ? d.precipitation_sum.length : 0;
  const out = [];
  for (let i = 0; i < n; i++) {
    out.push({
      day: at(d.time, i),
      precipitation: at(d.precipitation_sum, i),
      sunshine: d.sunshine_duration ? at(d.sunshine_duration, i) : 0,
      precipProb: at(d.precipitation_probability_max, i),
      tempMax: at(d.temperature_2m_max, i),
      weatherCode: at(d.weather_code, i),
    });
  }
  return out;
}

// The two booleans, exactly as the engine derives them. A null/undefined value
// compares false (`null > 2` is false in JS) — deliberately NOT coerced to 0 first,
// so the stored numbers stay honest (null = the API gave nothing).
export function classifyWeather(reading) {
  const precip = reading ? reading.precipitation : undefined;
  const sunshine = reading ? reading.sunshine : undefined;
  return { isRainy: precip > RAIN_MM_OVER, isSunny: sunshine > SUNSHINE_S_OVER };
}

// How far ahead the target is, the engine's way: target at 12:00Z minus "now",
// rounded to whole days. Negative for a past date.
export function daysAhead(dateIso, now = new Date()) {
  return Math.round((Date.parse(String(dateIso).slice(0, 10) + "T12:00:00Z") - now.getTime()) / 86400000);
}

// The engine only comments "Weather unavailable" on a target within 14 days.
export function unavailableFlagApplies(dateIso, now = new Date()) {
  return daysAhead(dateIso, now) <= UNAVAILABLE_FLAG_MAX_DAYS_AHEAD;
}

// A stored 1321 row → the same shape the direct call produces.
export function weatherFromStoredRow(row) {
  if (!row || typeof row !== "object") return null;
  return {
    status: "ok",
    isRainy: row.is_rainy === true,
    isSunny: row.is_sunny === true,
    source: "supabase",
    reading: { day: row.day ?? null, precipitation: row.rain_mm ?? null, sunshine: row.sunshine_s ?? null },
  };
}

// The ONE object the engine config carries (forecast.js DEFAULT_CONFIG.weather):
// { date, isRainy, isSunny, unavailableFlag, source } — or null, meaning "add nothing",
// which is what the engine does after a thrown fetch. Date-keyed on purpose: the
// engine only ever has the TARGET's weather, so contextMultipliers applies it to that
// date alone and a history day can never pick it up.
export function weatherForConfig(dateIso, result, now = new Date()) {
  const date = String(dateIso).slice(0, 10);
  if (!result || result.status === "error") return null;
  if (result.status === "unavailable") {
    return { date, isRainy: null, isSunny: null, unavailableFlag: unavailableFlagApplies(date, now), source: result.source || "open-meteo" };
  }
  return { date, isRainy: result.isRainy === true, isSunny: result.isSunny === true, unavailableFlag: false, source: result.source || "open-meteo" };
}

// IO. The engine's getWeather() for ONE date, with its exact outcome rules:
//   200 + a daily.precipitation_sum → { status:'ok', isRainy, isSunny, reading }
//   not-200, or no daily / no precipitation_sum → { status:'unavailable' }
//   a throw (network, abort/timeout, bad JSON) → { status:'error' }   (the engine's
//   swallowed exception: NO chip)
// Never throws itself. `fetchImpl` is injectable for tests; the default timeout
// (8 s) turns a hung call into the engine's "thrown" path rather than a stuck screen.
export async function fetchOpenMeteoDay(dateIso, { fetchImpl = globalThis.fetch, timeoutMs = 8000 } = {}) {
  const date = String(dateIso).slice(0, 10);
  const url = openMeteoUrl(date, date);
  const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctl ? setTimeout(() => ctl.abort(), timeoutMs) : null;
  let res;
  try {
    res = await fetchImpl(url, ctl ? { signal: ctl.signal } : undefined);
  } catch (e) {
    if (timer) clearTimeout(timer);
    return { status: "error", reason: e?.message || String(e), url };
  }
  if (timer) clearTimeout(timer);
  if (!res || res.status !== 200) return { status: "unavailable", httpStatus: res ? res.status : null, url };
  let json;
  try { json = await res.json(); }
  catch (e) { return { status: "error", reason: "unparseable body", url }; }
  const readings = parseOpenMeteoDaily(json);
  if (!readings) return { status: "unavailable", httpStatus: 200, url };
  // start_date === end_date, so index 0 IS the target — exactly how the engine reads it.
  const reading = readings[0] || null;
  return { status: "ok", ...classifyWeather(reading), reading, source: "open-meteo", url };
}
